/**
 * Клиент тонкого API (`core/api.py`) · Ф2.2
 *
 * Витрина ничего не считает — только просит и показывает. Подпись Telegram едет
 * заголовком X-Telegram-Init-Data на КАЖДОМ запросе (серверной сессии нет — это канон
 * Mini App и решение Ф2.1). Никаких telegram_id в теле: сервер берёт его из подписи.
 *
 * Базовый адрес по умолчанию — /api: в dev его проксирует vite на 127.0.0.1:8000,
 * значит витрина и API живут на ОДНОМ origin и наружу торчит один tunnel-URL.
 */

import { noteServerError } from "./feedback";
import type { LbOut } from "./leaderboard";
import type { Lost, Snapshot } from "./reset";
import { initData } from "./tg";
import type { XpGain, XpState } from "./xp";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

/**
 * Право доступа (окно 3). Считает `core/access.py`, витрина ничего не решает сама.
 * `reason`: gate_off · gift · subscription · cache · leniency · grace —
 * доступ есть; no_subscription — не подписан; unverified — Telegram не ответил.
 */
export interface Access {
  allowed: boolean;
  reason: string;
  source?: string | null;
  state?: string | null;
  checked_at?: string | null;
  detail?: string | null;
  channel: { url: string | null; on: boolean };
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly access?: Access) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * ⭐ Замок может защёлкнуться на ЛЮБОМ запросе (отписался, пока занимался), поэтому
 * ловим 403 здесь, в одном месте, а не в каждом экране: так ни один путь витрины не
 * останется с молчаливой ошибкой. Обработчик ставит `main.ts`.
 */
let onDenied: ((access: Access) => void) | null = null;

export function handleAccessDenied(fn: (access: Access) => void): void {
  onDenied = fn;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const signature = initData();
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      ...init,
      headers: {
        "X-Telegram-Init-Data": signature,
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(init?.headers || {}),
      },
    });
  } catch {
    // ⭐ Окно 5: приложение помнит последнюю беду за человека — в заявке «не работает»
    // это самая полезная строка, и сам он её никогда не напишет.
    noteServerError(0, path, "сервер не отвечает");
    throw new ApiError(0, "Сервер не отвечает");
  }
  if (!res.ok) {
    let detail = `Ошибка ${res.status}`;
    let access: Access | undefined;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === "string") {
        detail = body.detail;
      } else if (body.detail && typeof body.detail === "object") {
        // Отказ ворот приезжает объектом: человеческий текст + само право доступа.
        const wrapped = body.detail as { message?: string; access?: Access };
        if (wrapped.message) detail = wrapped.message;
        if (wrapped.access) access = wrapped.access;
      }
    } catch { /* тело не JSON — оставляем код */ }
    if (res.status === 403 && access) onDenied?.(access);
    noteServerError(res.status, path, detail);
    throw new ApiError(res.status, detail, access);
  }
  return (await res.json()) as T;
}

/**
 * Текст ошибки ДЛЯ ЧЕЛОВЕКА (правка 26.07 по живой обкатке).
 *
 * На экране у владельца оказалась «Ошибка 502» — это техтекст: код шлюза человеку не
 * говорит ничего, а починить он его не может. Оставляем человеку только то, что он
 * поймёт: своё, написанное по-русски объяснение от нашего же API (например «Подпись не
 * принята») мы показываем как есть, а голый код или ответ чужого шлюза заменяем
 * переданным человеческим текстом. ⛔ Механику не трогаем: кнопка «Отправить ещё раз»
 * и сам факт «ответ не сохранился» остаются — молча терять ответ нельзя.
 */
export function humanMessage(e: unknown, fallback: string): string {
  const detail = String((e as { message?: unknown })?.message ?? "").trim();
  if (!detail) return fallback;
  const technical =
    /^Ошибка \d+$/.test(detail) ||          // наш же запасной текст «Ошибка NNN»
    /^\s*</.test(detail) ||                 // HTML-страница шлюза
    /\b\d{3}\b/.test(detail) ||             // любой код внутри строки
    /^(bad|internal|service|gateway|not found|unauthorized|forbidden)/i.test(detail);
  return technical ? fallback : detail;
}

/* ── формы ответов (ровно то, что отдаёт core/api.py) ─────────────────────── */

export interface Settings {
  daily_new_words: number;
  daily_reviews_max: number;
  session_size: number;
  verbs_first: number;
  enrich_ahead_days: number;
  card_mode: string;
  tz: string;
  theme: string;
  /** Ф-D: «Звук» off / word / word_phrase */
  audio_mode?: string;
  /** Ф-D: «Проговаривать новое слово само» 0/1 — ⚠ после правки приезжает строкой */
  audio_autoplay?: string | number;
  /** Ф-D: «Скорость» normal / slow (slow = playbackRate 0.7, не второй набор файлов) */
  audio_speed?: string;
  /** Ф2.6: напоминать ли в Telegram — 0/1 (⚠ после правки приезжает строкой). */
  push_enabled?: string | number;
  /** Ф2.6: во сколько напоминать, «ЧЧ:ММ» ПО ЕГО поясу (`tz`). Шлёт `core/push.py`. */
  push_time?: string;
  /** 🆕 Окно 2 запуска: слово дня — включено и во сколько (своё, утреннее). */
  word_enabled?: string | number;
  word_time?: string;
  /** Окно 4: показывать ли имя другим в общей таблице — 0/1. */
  stats_opt_in?: string | number;
  /** 🆕 Фаза 3 «книги»: кегль книжной страницы s / m / l. */
  reader_size?: string;
  /** 🆕 Фаза 3 «книги»: подсветка слов в тексте off / learn / all. */
  reader_highlight?: string;
  [key: string]: string | number | undefined;
}

export interface DailyStats {
  date: string;
  due_total: number;
  due_reviews: number;
  due_new: number;
  new_left: number;
  reviews_left: number;
  answered_today: number;
  new_today: number;
  in_queue: number;
  session_size: number;
  skipped_inactive: number;
  streak: number;
  /** ⭐ Окно 2 запуска, ч. 2 — «ещё 5 новых». Все четыре считает `core/runner.py`;
   *  ⛔ витрина ни одного из этих чисел не выводит сама. Поля необязательные:
   *  старый сервер их не пришлёт, и кнопки тогда просто не будет. */
  extra_grants?: number;
  extra_new?: number;
  extra_left?: number;
  extra_step?: number;
}

export interface UserRef {
  user_id: number;
  telegram_id: number;
  name?: string;
  username?: string | null;
}

export interface AuthOut {
  /** ⚠ Окно 3: без доступа приходит `null` — человек в базе не заводится вовсе. */
  user: UserRef | null;
  created: boolean;
  seeded: { subgroups: { name: string; added: number; duplicates: number }[]; linked?: unknown } | null;
  /** Окно 3: есть ли доступ и почему. Отсутствует — значит ворота выключены. */
  access?: Access;
  settings: Settings | null;
  today: DailyStats | null;
}

export interface MeOut {
  user: UserRef;
  settings: Settings;
  today: DailyStats;
  /** Ф2.5, слой 3: опыт, уровень, доля кольца, серия, герой — считает `core/gamify.py`. */
  xp?: XpState;
  /** Калибровка на старте: показывать ли её вместо приложения (решает ядро). */
  calibration_needed?: boolean;
  vocabulary: {
    total: number;
    active: number;
    deferred: number;
    recognise: number;
    produce: number;
    learned: number;
  };
  achievements: { earned: number; total: number };
  /** Окно 5: показывать ли кнопку админки. ⛔ Дверь всё равно закрывает API (403). */
  admin?: boolean;
  /** Окно 5: пора ли спросить оценку — правило считает ядро. */
  feedback?: AskState;
}

/** Карточка = структура `lexicon.build_card()` как JSON + сторона вопроса от API. */
export interface Card {
  word_id: number;
  word: string;
  subgroup: string | null;
  status: string;
  box: number;
  next_show: string | null;
  part_of_speech: string | null;
  queue_pos: string | null;
  verb_badge: string | null;
  is_verb: boolean;
  enriched: boolean;
  translation: string | null;
  translations_all: string[];
  ipa: string | null;
  cefr: string | null;
  verb_usage: string | null;
  pos_all: string[];
  collocations: string[];
  context_note: string | null;
  /** 🆕 21.08: регистр слова из тематического набора — сила · с кем · мягче.
   *  ⛔ Витрина эти значения не считает: они лежат в датасете набора и приезжают
   *  из ядра (`lexicon.build_card`). У обычного слова — null. */
  register: {
    pack: string; strength: number; company: string;
    softer?: string | null; note?: string | null;
  } | null;
  domains: string[];
  distractors: string[];
  /** ⭐ М4: неверные АНГЛИЙСКИЕ слова для ступени 2 (RU→EN) — собирает ядро из словаря. */
  distractors_en?: string[];
  forms: Record<string, string>;
  lexicon_id: number | null;
  example: { en: string; ru: string | null } | null;
  /** id примера — адрес его озвучки: `GET /audio/phrase/{example_id}` (Ф-D). */
  example_id: number | null;
  example_kind: string | null;
  example_gapped: string | null;
  /** Форма слова в примере: строка, а у разрывного оборота — список (`["blew","up"]`). */
  example_form: string | string[] | null;
  /** Ш5: показан КОРОТКИЙ пример новичка (≤ 6 слов), а не длинный из прохода B. */
  example_short?: boolean;
  /** ⭐ М3: длинный пример вторым в разборе («а живьём это звучит так») — с бокса 2. */
  example_long?: { en: string; ru: string | null } | null;
  /** Его озвучка: `GET /audio/phrase/{example_long_id}`. */
  example_long_id?: number | null;
  /** ⭐ М3: «A» — чистое служебное (would), «B» — двойное (have), null — обычное слово. */
  function_word?: "A" | "B" | null;
  /** ⭐ М3: решение ЯДРА — спрашивать конструкцией (пропуском), а не переводом. */
  ask_construction?: boolean;
  /**
   * ⭐⭐ Окно 1 запуска: другие ОБОРОТЫ — кнопками ступени 3 («I _____ go now.» →
   * have to · going to · would like · used to). Список собирает ядро из того же датасета,
   * которым обороты залиты; ⛔ витрина его не хранит. Пусто = слово не оборот.
   */
  construction_peers?: string[];
  direction: "en_ru" | "ru_en";
  priority?: number | null;
  queue_is_verb?: boolean;
}

/** Калибровка на старте (`core/calibrate.py`). ⭐ `rules` — пороги ЯДРА: витрина ведёт
 *  человека по полосам по ним, а не по своей копии чисел. */
export interface CalibrationOut {
  needed: boolean;
  done_at: string | null;
  rules: {
    bands: { key: string; title: string }[];
    start_band: number;
    pack_size: number;
    up_known: number;
    down_known: number;
    max_taps: number;
    traps: string[];
  };
  words: Record<string, {
    word_id: number; word: string; ru: string;
    freq_rank: number | null; distractors: string[];
  }[]>;
}

/** Итог калибровки — считает ядро, витрина только показывает. */
export interface CalibrationResult {
  border: string;
  border_title: string;
  known: number;
  fan: number;
  fan_per_day: number;
  fan_days: number;
  opened_bands: number;
  trap_failed: boolean;
  snapshot: number;
}

export interface SessionOut {
  date: string;
  cards: Card[];
  stats: DailyStats;
  /** Только у ответа `/session/extra`: сколько слов дало ЭТО нажатие. */
  granted?: number;
  /** Сколько нажатий осталось сегодня (считает ядро). */
  extra_left?: number;
}

export interface AnswerOut {
  state: {
    word_id: number; word: string; grade: number; status: string;
    box: number; interval_days: number; next_show: string;
  };
  earned: { code: string; title: string; description: string }[];
  grade_label: string;
  /** Ф2.5: сколько опыта дал ЭТОТ ответ и не поднялся ли уровень. */
  xp?: XpGain;
  /** Ф2.5: состояние опыта ПОСЛЕ ответа (кольцо и маскот перерисовываются по нему). */
  progress?: XpState;
  stats: DailyStats;
}

export interface ProgressOut {
  vocabulary: {
    total: number; active: number; deferred: number;
    recognise: number; produce: number; learned: number;
    verbs_learned: number; nouns_learned: number;
    fam_learned: [number, number];
    fam_ceiling: [number, number];
    /** Ф2.4: словарь по частям речи — `lexicon.part_of_speech`, правда о языке. */
    by_pos: Record<string, { total: number; new: number; learning: number; known: number }>;
  };
  ladder: { label: string; low: number; high: number; what: string; src: string }[];
  /** Ф2.4: народный ориентир владельца (⚠ НЕ научная норма — так и подписан). */
  owner_scale: { name: string; verb: number; noun: number }[];
  you_are_here: { families: [number, number]; band: string | null };
  ceiling: { families: [number, number]; band: string | null };
  /** Пересчёт «единицы словаря → семьи слов»: [0.85, 0.87] (измерено по NGSL). */
  family_ratio: [number, number];
  production_box: number;
}

/** Ф2.4: каталог наград с прогрессом + начисленные (`core/achievements.py`). */
export interface AchievementsOut {
  catalog: {
    code: string; title: string; rule_type: string;
    value: number; threshold: number; earned: boolean;
  }[];
  earned: { code: string; title: string; description: string; earned_at: string }[];
  /** Ф2.5: сколько опыта даёт награда — цифра из ядра (`gamify.XP_ACHIEVEMENT`). */
  xp_reward?: number;
}

/* ── вызовы ───────────────────────────────────────────────────────────────── */

export const health = () => call<{ ok: boolean; api: string; db: string }>("/health");
export const auth = () => call<AuthOut>("/auth", { method: "POST" });
export const me = () => call<MeOut>("/me");
export const progress = () => call<ProgressOut>("/progress");
export const achievements = () => call<AchievementsOut>("/achievements");

/**
 * Окно 4: статистика участников. Все три таблицы приезжают одним запросом — вкладки
 * на экране переключаются без обращения к серверу. Считает `core/leaderboard.py`.
 */
export const leaderboard = () => call<LbOut>("/leaderboard");
/** Калибровка: нужна ли, слова полос и пороги ядра. */
export const calibration = () => call<CalibrationOut>("/calibration");

/**
 * Ответы калибровки → настроенный словарь. ⛔ Ни границы, ни судьбы слов не шлём:
 * их считает ядро. ⛔ И чей словарь настраивать — тоже не шлём, это подпись.
 */
export const calibrationApply = (
  body: { answers: { word_id: number; band: string; known: boolean }[]; trap_failed: boolean },
) => call<CalibrationResult>("/calibration/apply", {
  method: "POST",
  body: JSON.stringify(body),
});

export const session = () => call<SessionOut>("/session");

/**
 * «Ещё 5 новых»: надбавка к дневной норме НА СЕГОДНЯ (окно 2 запуска, ч. 2).
 * ⛔ Тела у запроса нет вовсе — чей лимит поднимать, решает подпись, а не витрина.
 * Потолок дня приезжает как 429, и это не поломка, а «на сегодня хватит».
 */
export const sessionExtra = () =>
  call<SessionOut>("/session/extra", { method: "POST" });
export const card = (wordId: number) => call<Card>(`/card/${wordId}`);

/** Оценка 0/1/2 → `runner.answer` в ядре. Витрина сама SRS не считает. */
export const answer = (wordId: number, grade: 0 | 1 | 2) =>
  call<AnswerOut>("/answer", {
    method: "POST",
    body: JSON.stringify({ word_id: wordId, grade }),
  });

/**
 * Озвучка (Ф-D). Отдельно от `call<T>`, потому что ответ — mp3, а не JSON.
 * ⚠ Почему не `<audio src="…">`: тег не умеет слать заголовок с подписью Telegram,
 * а API проверяет её на КАЖДОМ запросе (папка звука статикой не открыта). Поэтому
 * тянем файл сами и отдаём плееру `blob:`-адрес (`src/audio.ts`).
 * Кэш браузера при этом работает: у файла приватный ETag → повтор получает 304.
 */
export async function audioBlob(kind: "word" | "phrase", id: number): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/audio/${kind}/${id}`, {
      headers: { "X-Telegram-Init-Data": initData() },
    });
  } catch {
    throw new ApiError(0, "Звук не доехал");
  }
  // 404 = «ещё не озвучено» (пустая колонка в базе) — для плеера это отдельный случай.
  if (!res.ok) throw new ApiError(res.status, res.status === 404 ? "Озвучки нет" : "Звук не доехал");
  return res.blob();
}

/**
 * Ф2.5: сменить героя. Своим адресом, а не через `PATCH /settings` — персонаж живёт
 * в слое 3 (`user_progress.character`), и второго источника правды о нём быть не должно.
 */
export const setCharacter = (character: string) =>
  call<{ xp: XpState }>("/character", {
    method: "POST",
    body: JSON.stringify({ character }),
  });

/* ── книги: полка, страница, закладка (Фаза 3 окна «книги») ───────────────── */

/** Книга на полке. Все цифры считает ядро (`books.shelf`), витрина только раскладывает. */
export interface BookRow {
  slug: string;
  title: string;
  author: string | null;
  words_total: number;
  paragraphs: number;
  /** Доля слов книги, которые словарь может объяснить сам (0..1). */
  coverage: number | null;
  median_sentence: number | null;
  source_id: string | null;
  started: boolean;
  idx: number;
  /** ⭐ С какого абзаца открывать книгу: титул, посвящение и оглавление пролистываются
   *  сами, но остаются в книге — назад к ним можно, как в бумажной. Это ДАННЫЕ. */
  start_idx: number;
  percent: number;
  words_left: number;
  minutes_left: number;
}

/**
 * Разбор одного слова страницы. ⭐ Приезжает ВМЕСТЕ с текстом, а не по тапу: страница —
 * это ~90 РАЗНЫХ слов, словарь на них невелик, зато карточка открывается мгновенно.
 * `known: false` = слова нет в нашем словаре (в подготовленной книге такого быть не должно).
 */
export interface BookWord {
  known: boolean;
  lemma?: string | null;
  lexicon_id?: number | null;
  translation?: string | null;
  ipa?: string | null;
  part_of_speech?: string | null;
  is_form?: boolean;
  note?: string | null;
  /** Состояние слова У ЭТОГО человека: null = ещё не начинал. */
  status?: string | null;
  box?: number | null;
}

export interface BookPage {
  slug: string;
  title: string;
  author: string | null;
  start: number;
  count: number;
  total: number;
  paragraphs: string[];
  words: Record<string, BookWord>;
  /** Имена собственные на этих абзацах — ⛔ они не тапаются, это не слова языка. */
  names: string[];
}

export interface Position { idx: number; max_idx: number; total: number;
                            started: boolean; percent: number }

export const books = () => call<{ books: BookRow[]; wpm: number }>("/books");
export const book = (slug: string) => call<BookRow>(`/books/${encodeURIComponent(slug)}`);
export const bookText = (slug: string, start: number, count: number) =>
  call<BookPage>(`/books/${encodeURIComponent(slug)}/text?start=${start}&count=${count}`);

/** Закладка. ⛔ Чью — не спрашиваем: человек берётся из подписи. */
export const setBookPosition = (slug: string, idx: number) =>
  call<Position>(`/books/${encodeURIComponent(slug)}/position`, {
    method: "POST",
    body: JSON.stringify({ idx }),
  });

/* ── обратная связь и админка (окно 5) ────────────────────────────────────── */

/** Правило «пора ли спрашивать оценку» считает ядро (`feedback.should_ask`). */
export interface AskState { ask: boolean; why: string; days: number; need_days?: number }

export interface Ticket {
  id: number;
  user_id: number;
  name: string | null;
  text: string;
  context: Record<string, string>;
  status: string;
  status_label: string;
  answer: string | null;
  notified_status: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackOut {
  ask: AskState;
  /** Мои заявки со статусами: «принято → чиним → готово» видно и в приложении. */
  my: Ticket[];
  statuses: { key: string; label: string }[];
}

export interface AdminOut {
  people: { users: number; new_7d: number; allowed: number; gifts: number; denied: number };
  activity: { answers_24h: number; answers_7d: number; answers_total: number;
              push_failed_7d: number };
  server: { db: string; db_bytes: number; tables: number;
            backup: { name: string; bytes: number; at: string; count: number } | null };
  study: { studying: number; total_members: number | null; rows: unknown[] };
  ratings: { count: number; average: number | null; distribution: Record<string, number>;
             wishes: { id: number; name: string; stars: number; text: string; day: string }[] };
  tickets: Ticket[];
  tickets_open: number;
  statuses: { key: string; label: string }[];
  notified: { sent: number; failed: number };
  channel: { id: string; url: string | null; on: boolean };
}

export const feedbackState = () => call<FeedbackOut>("/feedback");

export const rateApp = (stars: number, text?: string) =>
  call<{ saved: boolean; repeat: boolean }>("/feedback/rating", {
    method: "POST",
    body: JSON.stringify(text ? { stars, text } : { stars }),
  });

export const skipRating = () =>
  call<{ saved: boolean }>("/feedback/skip", { method: "POST" });

export const sendTicket = (text: string, context: Record<string, string>) =>
  call<Ticket>("/feedback/ticket", {
    method: "POST",
    body: JSON.stringify({ text, context }),
  });

/** ⛔ Постороннему сюда — 403 от API, а не спрятанная кнопка (решает ядро). */
export const adminView = () => call<AdminOut>("/admin");

export const adminTicket = (ticketId: number, status: string, answer?: string) =>
  call<{ ticket: Ticket; notified: { sent: number; failed: number } }>("/admin/ticket", {
    method: "POST",
    body: JSON.stringify(answer ? { ticket_id: ticketId, status, answer }
                                : { ticket_id: ticketId, status }),
  });

export const adminGift = (telegramId: number, name?: string, revoke = false) =>
  call<Record<string, unknown>>("/admin/gift", {
    method: "POST",
    body: JSON.stringify({ telegram_id: telegramId, name, revoke }),
  });

/* ── «начать с нуля» (окно 6) ─────────────────────────────────────────────── */

export interface ResetOut {
  /** ⭐ Фраза подтверждения и срок отката приходят ОТ ЯДРА, витрина их не выдумывает. */
  confirm_phrase: string;
  ttl_days: number;
  scopes: { key: string; title: string; what: string; keeps: string }[];
  lost: Lost;
  snapshots: Snapshot[];
}

export const resetState = () => call<ResetOut>("/reset");

/**
 * ⛔ Три РАЗНЫХ адреса, а не один с флагом: у кнопок разный вес.
 * ⛔ И никакого «чьё сбрасывать» в теле — сервер берёт человека из подписи Telegram.
 */
export const resetScope = (scope: string, confirm: string) =>
  call<{ scope: string; title: string; rows: number; snapshot: number;
         undo_until: string; state: ResetOut }>(`/reset/${scope}`, {
    method: "POST",
    body: JSON.stringify({ confirm }),
  });

export const resetUndo = (snapshotId?: number) =>
  call<{ scope: string; title: string; restored: number; state: ResetOut }>("/reset/undo", {
    method: "POST",
    body: JSON.stringify(snapshotId ? { snapshot_id: snapshotId } : {}),
  });

export const patchSettings = (changes: Record<string, string | number>) =>
  call<{ settings: Settings; changed: string[] }>("/settings", {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
